import React, {useEffect, useState} from 'react';
import Link from 'next/link';
import {
  getItemOfferHistories,
  ItemInterface,
  ListingType,
  MintStatus,
  OfferInterface,
  OfferItemInterface,
} from '@api/item';
import {useRouter} from 'next/router';
import {TextEllipsis} from 'neka-simple-utilities';
import AcceptOfferModal from '@components/modal/accept_offer_modal';
import {UserInterface} from '@api/user';

const OfferTab = ({
  item,
  user,
  onRefresh,
  setOnRefresh,
}: {
  item: ItemInterface;
  user: UserInterface;
  onRefresh: string;
  setOnRefresh?: (value: number) => void;
}) => {
  const router = useRouter();
  const id = router.query.id;

  const [offerData, setOfferData] = useState<OfferItemInterface[]>([]);
  const [acceptOfferModalShown, setAcceptOfferModalShown] = useState<boolean>(false);
  const [offerToAccept, setOfferToAccept] = useState<OfferInterface | undefined>(undefined);
  const [isOwner, setIsOwner] = useState<boolean>(false);

  const getItemOfferData = async (itemId: number, page: number) => {
    const nowDate = new Date();
    const milisecondDate = nowDate.getMilliseconds() / 1000;
    const data = await getItemOfferHistories(itemId, page)
      .then((res) => res)
      .catch(() => ({records: []}));
    setOfferData(
      data?.records?.map((val) => {
        return {
          ...val,
          isExpired: val.expirationTimestamp > milisecondDate,
        };
      }),
    );
    return data;
  };

  useEffect(() => {
    if (!id) return;
    getItemOfferData(Number(id), 1);
  }, [id, onRefresh]);

  const getQuantityOwned = (item: ItemInterface) => {
    const owned = item?.owners?.find((owner: {wallet_address: string; mintStatus: MintStatus}) => {
      if (owner.wallet_address === user?.wallet_address && owner.mintStatus == MintStatus.MINTED)
        return true;
      else return false;
    });
    if (owned) return owned.quantity;
    return 0;
  };

  const ownedEnoughItems = (offer: OfferInterface) => {
    return Number(offer.quantity) <= getQuantityOwned(item);
  };

  const isDirectListing = () => {
    return item?.ItemActiveListings?.[0]?.listingType == ListingType.Direct;
  };

  const isLister = () => {
    return item?.ItemActiveListings?.[0]?.lister.wallet_address == user.wallet_address;
  };

  const isExpired = (endTime: number) => {
    return endTime <= Math.floor(Date.now() / 1000);
  };

  const isMakingOffer = (address: string) => {
    return address === user.wallet_address;
  };

  const showAcceptOfferModal = (offer: OfferInterface) => {
    setOfferToAccept(offer);
    setAcceptOfferModalShown(true);
  };

  const hideAcceptOfferModal = () => {
    setOnRefresh?.(Math.floor(Date.now() / 1000));
    setOfferToAccept(undefined);
    setAcceptOfferModalShown(false);
  };

  return (
    <>
      {/* <!-- Offers --> */}
      {offerData?.length == 0 ? (
        <div className="flex justify-center">
          <p className="text-dark-navy dark:text-white text-lg font-normal font-display ">
            No offers yet
          </p>
        </div>
      ) : (
        <div
          className="tab-pane fade show active"
          id="offers"
          role="tabpanel"
          aria-labelledby="offers-tab"
        >
          <div
            role="table"
            className="scrollbar-custom dark:bg-black-green-2 max-h-72 w-full overflow-y-auto rounded-none bg-white text-sm dark:text-white"
          >
            <div className="sticky top-0 flex" role="row">
              <div
                className="w-[25%] min-w-[100px] dark:bg-black-green bg-light-grey py-2 px-4"
                role="columnheader"
              >
                <span className="text-dark-navy dark:text-white w-full overflow-hidden text-ellipsis">
                  Price/Token
                </span>
              </div>
              <div
                className="w-[25%] min-w-[100px] dark:bg-black-green bg-light-grey py-2 px-4"
                role="columnheader"
              >
                <span className="text-dark-navy dark:text-white w-full overflow-hidden text-ellipsis">
                  Quantity
                </span>
              </div>

              <div
                className="w-[25%] min-w-[100px] dark:bg-black-green bg-light-grey py-2 px-4"
                role="columnheader"
              >
                <span className="text-dark-navy dark:text-white w-full overflow-hidden text-ellipsis">
                  Floor Difference
                </span>
              </div>
              <div
                className="w-[25%] min-w-[100px] dark:bg-black-green bg-light-grey py-2 px-4"
                role="columnheader"
              >
                <span className="text-dark-navy dark:text-white w-full overflow-hidden text-ellipsis">
                  Expiration
                </span>
              </div>
              <div
                className="w-[25%] min-w-[100px] dark:bg-black-green bg-light-grey py-2 px-4"
                role="columnheader"
              >
                <span className="text-dark-navy dark:text-white w-full overflow-hidden text-ellipsis ">
                  From
                </span>
              </div>

              <div
                className="w-[25%] min-w-[100px] dark:bg-black-green bg-light-grey py-2 px-4"
                role="columnheader"
              >
                <span className="text-dark-navy dark:text-white w-full overflow-hidden text-ellipsis ">
                  Action
                </span>
              </div>
            </div>
            {offerData?.map((offer, index) => (
              <div className="flex" role="row" key={index}>
                <div
                  className="w-[25%] min-w-[100px] dark:border-black-green border-jacarta-100 flex items-center px-4 whitespace-nowrap border-t py-4 "
                  role="cell"
                >
                  <span className="dark:text-white text-base tracking-tight">
                    {offer.pricePerToken}{' '}
                  </span>
                </div>
                <div
                  className="items-center w-[25%] min-w-[100px] dark:border-black-green border-jacarta-100 flex whitespace-nowrap border-t py-4 px-4"
                  role="cell"
                >
                  <span className="dark:text-white tracking-tight w-[100%] text-right md:text-center lg:text-left">
                    {offer.quantity}
                  </span>
                </div>
                <div
                  className="w-[25%] min-w-[100px] dark:border-black-green border-jacarta-100 flex items-center flex-wrap border-t py-4 px-4"
                  role="cell"
                >
                  <span className="dark:text-white tracking-tight">{offer.floorDifference}</span>
                </div>
                <div
                  className="w-[25%] min-w-[100px] dark:border-black-green border-jacarta-100 flex border-t py-4 px-4"
                  role="cell"
                >
                  <span className="dark:text-white tracking-tight">{offer.expiration}</span>
                </div>
                <div
                  className="w-[25%] min-w-[100px] dark:border-black-green border-jacarta-100 flex items-center px-4 border-t py-4"
                  role="cell"
                >
                  <Link href={`/user/${offer.fromAddress}`}>
                    {offer.offeror ? (
                      <a className="text-aqua-blue">
                        {TextEllipsis({
                          text: offer.offeror as string,
                          position: 'middle',
                          length: 4,
                        })}
                      </a>
                    ) : (
                      <a className="text-aqua-blue">
                        {TextEllipsis({
                          text: offer.fromAddress as string,
                          position: 'middle',
                          length: 4,
                        })}
                      </a>
                    )}
                  </Link>
                </div>
                {ownedEnoughItems(offer) && !isMakingOffer(offer.fromAddress) ? (
                  !isExpired(offer.expirationTimestamp) ? (
                    <div
                      className="w-[25%] min-w-[100px] dark:border-black-green border-jacarta-100 flex px-4 border-t py-4 "
                      role="cell"
                    >
                      <button
                        className="w-max font-display text-base font-semibold text-transparent bg-clip-text bg-green-gradation-light cursor-pointer"
                        onClick={() => showAcceptOfferModal(offer)}
                      >
                        Accept
                      </button>
                    </div>
                  ) : (
                    <div
                      className="w-[25%] min-w-[100px] dark:border-black-green border-jacarta-100 flex px-4 border-t py-4 "
                      role="cell"
                    >
                      <div className="w-max font-display  font-semibold text-transparent bg-clip-text bg-grey">
                        Expired
                      </div>
                    </div>
                  )
                ) : (
                  <div
                    className="w-[25%] min-w-[100px] dark:border-black-green border-jacarta-100 flex items-center border-t py-4 px-4"
                    role="cell"
                  ></div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <AcceptOfferModal
        openModal={acceptOfferModalShown}
        onClose={hideAcceptOfferModal}
        item={item}
        offer={offerToAccept as OfferInterface}
        user={user}
      />
    </>
  );
};

export default OfferTab;
